import { FaTiktok, FaInstagram, FaFacebookF, FaYoutube } from "react-icons/fa";

export function getCampaignMintSpaceId(campaign) {
  if (!campaign?.mintSpace) return null;
  return typeof campaign.mintSpace === "object"
    ? campaign.mintSpace._id
    : campaign.mintSpace;
}

/** Opens mint space Creator Rewards list with this campaign highlighted (discover entry). */
export function getCampaignListPath(campaign) {
  const mintSpaceId = getCampaignMintSpaceId(campaign);
  if (mintSpaceId && campaign?._id) {
    return `/mint-space/${mintSpaceId}?campaign=${campaign._id}&nav=campaigns`;
  }
  return getCampaignDetailsPath(campaign);
}

/** Direct campaign details URL (list click-through, share, bookmarks). */
export function getCampaignDetailsPath(campaign) {
  const mintSpaceId = getCampaignMintSpaceId(campaign);
  if (mintSpaceId) {
    return `/mint-space/${mintSpaceId}/campaigns/${campaign._id}`;
  }
  return `/campaigns/${campaign._id}`;
}

export function getCampaignSharePath(campaign) {
  return getCampaignDetailsPath(campaign);
}

export function getPlatformIcons(platforms) {
  const icons = [];
  if (platforms?.tiktok) icons.push(<FaTiktok key="tiktok" className="h-4 w-4" />);
  if (platforms?.instagramReels)
    icons.push(<FaInstagram key="instagram" className="h-4 w-4" />);
  if (platforms?.facebookReels)
    icons.push(<FaFacebookF key="facebook" className="h-4 w-4" />);
  if (platforms?.youtubeShorts || platforms?.youtubeLongForm)
    icons.push(<FaYoutube key="youtube" className="h-4 w-4" />);
  return icons;
}

export function formatCampaignDate(t, dateString) {
  if (!dateString) return t("campaigns.noEndDate") || "No end date";
  const date = new Date(dateString);
  const monthNames = [
    t("common.monthJan"),
    t("common.monthFeb"),
    t("common.monthMar"),
    t("common.monthApr"),
    t("common.monthMay"),
    t("common.monthJun"),
    t("common.monthJul"),
    t("common.monthAug"),
    t("common.monthSep"),
    t("common.monthOct"),
    t("common.monthNov"),
    t("common.monthDec"),
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return t("common.dateFormat", { month, day, year });
}

export function getParticipantTypeLabel(t, type) {
  switch (type) {
    case "creators":
      return t("campaigns.ugc");
    case "clippers":
      return t("campaigns.clipping");
    case "both":
      return t("campaigns.ugcAndClipping");
    default:
      return t("campaigns.ugc");
  }
}

export function getCampaignStatusBadge(t, status) {
  const statusColors = {
    active: "from-green-500 to-emerald-500",
    pending: "from-yellow-500 to-amber-500",
    paused: "from-slate-500 to-slate-600",
    completed: "from-blue-500 to-cyan-500",
    cancelled: "from-red-500 to-rose-500",
  };

  const statusLabels = {
    active: t("common.statusActive"),
    pending: t("common.statusPending"),
    paused: t("common.statusPaused"),
    completed: t("common.statusCompleted"),
    cancelled: t("common.statusCancelled"),
  };

  return (
    <span
      className={`cc-status-badge rounded-full bg-gradient-to-r ${
        statusColors[status] || statusColors.pending
      } font-bold text-white`}
    >
      {statusLabels[status] || statusLabels.pending}
    </span>
  );
}

export function formatCampaignNumber(num) {
  if (num === 0 || !num) return "0";
  return Number(num).toLocaleString();
}

/** Currency amounts (paid out, budget) — always 2 decimal places. */
export function formatCampaignMoney(num) {
  const n = Number(num);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function getCampaignBudgetPercentage(campaign) {
  if (!campaign.budget || campaign.budget === 0) return 0;
  return Math.min(((campaign.spent || 0) / campaign.budget) * 100, 100);
}

export function isCampaignBudgetExhausted(campaign) {
  return (campaign.budget || 0) > 0 && (campaign.spent || 0) >= campaign.budget;
}
